import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Input,
  Label,
  Select,
  Spinner,
  Subtitle1,
  Text,
} from "@fluentui/react-components";
import { ArrowLeft20Regular } from "@fluentui/react-icons";
import { authClient } from "../../utils/auth-client";
import { apiUrl } from "../../utils/api";
import { PermissionGrid } from "../../components/rbac/PermissionGrid";
import {
  ALL_PERMISSIONS,
  AppRole,
  TeamRole,
} from "../../components/rbac/permissionConstants";
import { UserCreateDialog } from "../../components/rbac/UserCreateDialog";
import { TeamCreateDialog } from "../../components/rbac/TeamCreateDialog";
import { bestaetigen, hinweisen } from "../../components/redesign/bestaetigen";

type RawUser = {
  id: string;
  email?: string;
  name?: string;
  role?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  department?: string;
  banned?: boolean;
  banReason?: string | null;
};

type AppTeamMemberDto = { id: string; userId: string; teamRole: TeamRole };
type AppTeamPermissionDto = { id: string; permission: string };
type AppTeamDto = {
  id: string;
  name: string;
  description?: string | null;
  members: AppTeamMemberDto[];
  permissions: AppTeamPermissionDto[];
};

type View =
  | { kind: "list" }
  | { kind: "user"; userId: string }
  | { kind: "team"; teamId: string };

const userDisplay = (u: RawUser): string => {
  const last = u.lastName || "";
  const first = u.firstName || "";
  if (last && first) return `${last}, ${first}`;
  return last || first || u.name || u.email || u.id;
};

const isPrivilegedRole = (role?: string) => role === "admin" || role === "gfgl";

export const UserManagementSection: React.FC = () => {
  const [users, setUsers] = useState<RawUser[]>([]);
  const [teams, setTeams] = useState<AppTeamDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [view, setView] = useState<View>({ kind: "list" });

  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadUsers(), loadTeams()]);
    } catch (e: any) {
      setError(e?.message || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    const { data, error: err } = await authClient.admin.listUsers({
      query: { limit: 200 },
    });
    if (err) throw new Error(err.message || "listUsers failed");
    setUsers((data?.users as RawUser[]) || []);
  };

  const loadTeams = async () => {
    const res = await fetch(apiUrl("rbac/teams"));
    if (!res.ok) throw new Error("Teams konnten nicht geladen werden");
    setTeams((await res.json()) as AppTeamDto[]);
  };

  const usersById = useMemo(() => {
    const m = new Map<string, RawUser>();
    for (const u of users) m.set(u.id, u);
    return m;
  }, [users]);

  // Split pending (self-provisioned via Microsoft, awaiting role/team
  // assignment) from active users. Pending lives in its own table at the
  // top of the page so admins immediately notice new sign-ups.
  const pendingUsers = useMemo(
    () => users.filter((u) => u.banned === true),
    [users],
  );
  const activeUsers = useMemo(
    () => users.filter((u) => !u.banned),
    [users],
  );

  const teamsByUserId = useMemo(() => {
    const m = new Map<string, Array<{ team: AppTeamDto; teamRole: TeamRole }>>();
    for (const t of teams) {
      for (const mem of t.members) {
        const arr = m.get(mem.userId) ?? [];
        arr.push({ team: t, teamRole: mem.teamRole });
        m.set(mem.userId, arr);
      }
    }
    return m;
  }, [teams]);

  const userEffectivePermissions = (user: RawUser): Set<string> => {
    if (isPrivilegedRole(user.role)) return new Set(ALL_PERMISSIONS);
    const perms = new Set<string>();
    // Service (08.10.2026): Kontakte anlegen/ansehen/bearbeiten, Vorgänge wie
    // Mitarbeiter — Stand der Rechte-Matrix, plus Team-Rechte falls im Team.
    if (user.role === "service") {
      for (const p of ["contacts:create", "contacts:read", "contacts:update", "vorgaenge:create", "vorgaenge:read", "vorgaenge:update", "vorgaenge:delete"]) {
        perms.add(p);
      }
    }
    for (const t of teams) {
      const isMember = t.members.some((m) => m.userId === user.id);
      if (!isMember) continue;
      for (const p of t.permissions) perms.add(p.permission);
    }
    return perms;
  };

  // ---- Mutations ----

  const setTeamPermissions = async (teamId: string, permissions: string[]) => {
    const res = await fetch(apiUrl(`rbac/teams/${teamId}/permissions`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ permissions }),
    });
    if (!res.ok) {
      void hinweisen({ titel: "Berechtigungen nicht gespeichert", text: "Die Berechtigungen des Teams konnten nicht gespeichert werden.", ton: "danger" });
      return;
    }
    await loadTeams();
  };

  const togglePerm = async (team: AppTeamDto, permission: string, checked: boolean) => {
    const next = new Set(team.permissions.map((p) => p.permission));
    if (checked) next.add(permission);
    else next.delete(permission);
    await setTeamPermissions(team.id, Array.from(next));
  };

  const removeMember = async (teamId: string, userId: string) => {
    if (
      !(await bestaetigen({
        titel: "Mitglied entfernen?",
        text: "Die Person verliert damit die Rechte, die sie über dieses Team hat.",
        ja: "Entfernen",
        ton: "danger",
      }))
    )
      return;
    const res = await fetch(apiUrl(`rbac/teams/${teamId}/members/${userId}`), {
      method: "DELETE",
    });
    if (res.ok) await loadTeams();
    else void hinweisen({ titel: "Entfernen fehlgeschlagen", text: "Das Mitglied konnte nicht aus dem Team entfernt werden.", ton: "danger" });
  };

  const changeMemberRole = async (
    teamId: string,
    userId: string,
    teamRole: TeamRole,
  ) => {
    const res = await fetch(apiUrl(`rbac/teams/${teamId}/members/${userId}/role`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ teamRole }),
    });
    if (res.ok) await loadTeams();
    else void hinweisen({ titel: "Rolle nicht geändert", text: "Die Team-Rolle konnte nicht aktualisiert werden.", ton: "danger" });
  };

  const addMember = async (teamId: string, userId: string, teamRole: TeamRole) => {
    const res = await fetch(apiUrl(`rbac/teams/${teamId}/members`), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId, teamRole }),
    });
    if (res.ok) await loadTeams();
    else void hinweisen({ titel: "Hinzufügen fehlgeschlagen", text: "Das Mitglied konnte nicht zum Team hinzugefügt werden.", ton: "danger" });
  };

  const deleteTeam = async (teamId: string) => {
    if (
      !(await bestaetigen({
        titel: "Team löschen?",
        text: "Alle Mitglieder verlieren die Rechte, die sie über dieses Team haben.",
        ja: "Löschen",
        ton: "danger",
      }))
    )
      return;
    const res = await fetch(apiUrl(`rbac/teams/${teamId}`), {
      method: "DELETE",
    });
    if (res.ok) {
      setView({ kind: "list" });
      await loadTeams();
    } else void hinweisen({ titel: "Löschen fehlgeschlagen", text: "Das Team konnte nicht gelöscht werden.", ton: "danger" });
  };

  const deleteUser = async (user: RawUser) => {
    if (
      !(await bestaetigen({
        titel: "Benutzer löschen?",
        text: `${user.email || user.id} kann sich danach nicht mehr anmelden.`,
        ja: "Löschen",
        ton: "danger",
      }))
    )
      return;
    const adminAny = authClient.admin as any;
    if (typeof adminAny.removeUser === "function") {
      const { error: err } = await adminAny.removeUser({ userId: user.id });
      if (err) {
        void hinweisen({ titel: "Löschen fehlgeschlagen", text: err?.message || "Der Benutzer konnte nicht gelöscht werden.", ton: "danger" });
        return;
      }
    } else {
      void hinweisen({ titel: "Löschen nicht möglich", text: "Diese Version der Anmeldung (Better Auth) kann Benutzer nicht löschen.", ton: "danger" });
      return;
    }
    setView({ kind: "list" });
    await loadAll();
  };

  // ---- View routing ----

  if (loading) {
    return (
      <Card style={{ padding: 20 }}>
        <Spinner label="Lade Benutzer und Teams..." />
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20 }}>
        <Text style={{ color: "#a00" }}>{error}</Text>
      </Card>
    );
  }

  if (view.kind === "user") {
    const user = usersById.get(view.userId);
    if (!user) {
      setView({ kind: "list" });
      return null;
    }
    return (
      <UserDetailCard
        user={user}
        memberships={teamsByUserId.get(user.id) ?? []}
        effectivePermissions={userEffectivePermissions(user)}
        onBack={() => setView({ kind: "list" })}
        onOpenTeam={(teamId) => setView({ kind: "team", teamId })}
        onSaved={loadAll}
        onDeleted={async () => {
          setView({ kind: "list" });
          await deleteUser(user);
        }}
      />
    );
  }

  if (view.kind === "team") {
    const team = teams.find((t) => t.id === view.teamId);
    if (!team) {
      setView({ kind: "list" });
      return null;
    }
    const teamPermSet = new Set(team.permissions.map((p) => p.permission));
    const usersAvailable = users.filter(
      (u) => !team.members.some((m) => m.userId === u.id),
    );

    return (
      <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 16 }}>
        <Header
          onBack={() => setView({ kind: "list" })}
          title={`Team: ${team.name}`}
          subtitle={team.description ?? undefined}
        />
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <Text weight="semibold">Mitglieder ({team.members.length})</Text>
            <AddMemberInline
              teamId={team.id}
              candidates={usersAvailable}
              onAdd={addMember}
            />
          </div>
          {team.members.length === 0 ? (
            <Text size={200}>Keine Mitglieder</Text>
          ) : (
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                  <th style={{ padding: 6 }}>Name</th>
                  <th style={{ padding: 6 }}>Kürzel</th>
                  <th style={{ padding: 6 }}>Team-Rolle</th>
                  <th style={{ padding: 6 }}>FO/BO</th>
                  <th style={{ padding: 6, textAlign: "right" }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {team.members.map((mem) => {
                  const u = usersById.get(mem.userId);
                  return (
                    <tr key={mem.id} style={{ borderBottom: "1px solid #f1f1f1" }}>
                      <td style={{ padding: 6 }}>
                        <Button
                          appearance="subtle"
                          onClick={() => u && setView({ kind: "user", userId: u.id })}
                        >
                          {u ? userDisplay(u) : mem.userId}
                        </Button>
                      </td>
                      <td style={{ padding: 6 }}>{u?.username ?? "—"}</td>
                      <td style={{ padding: 6 }}>
                        <Select
                          value={mem.teamRole}
                          onChange={(_, d) =>
                            changeMemberRole(team.id, mem.userId, d.value as TeamRole)
                          }
                        >
                          <option value="teammitglied">Teammitglied</option>
                          <option value="teamleiter">Teamleiter</option>
                        </Select>
                      </td>
                      <td style={{ padding: 6 }}>{u?.department ?? "—"}</td>
                      <td style={{ padding: 6, textAlign: "right" }}>
                        <Button size="small" appearance="subtle" onClick={() => removeMember(team.id, mem.userId)}>
                          Entfernen
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        <div>
          <Text weight="semibold">Team-Berechtigungen</Text>
          <Text size={200} style={{ display: "block", color: "#666", marginBottom: 6 }}>
            Welche Aktionen Teammitglieder auf den Datensätzen ihres Teams ausführen dürfen.
          </Text>
          <PermissionGrid
            selected={teamPermSet}
            onToggle={(perm, checked) => togglePerm(team, perm, checked)}
          />
        </div>
        <div>
          <Button appearance="subtle" onClick={() => deleteTeam(team.id)}>Team löschen</Button>
        </div>
      </Card>
    );
  }

  // ---- LIST view ----
  return (
    <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <Subtitle1>Benutzerverwaltung</Subtitle1>
        <div style={{ display: "flex", gap: 8 }}>
          <Button appearance="primary" onClick={() => setShowCreateUser(true)}>Benutzer erstellen</Button>
          <Button appearance="secondary" onClick={() => setShowCreateTeam(true)}>Neues Team</Button>
        </div>
      </div>

      {pendingUsers.length > 0 && (
        <section>
          <Text weight="semibold" size={400}>
            Wartet auf Freigabe ({pendingUsers.length})
          </Text>
          <Text size={200} style={{ display: "block", color: "#666", marginBottom: 8 }}>
            Neue Benutzer aus Microsoft Entra — Rolle und optional Team(s) zuweisen, dann freigeben.
          </Text>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ background: "#fff8e1", textAlign: "left" }}>
                  <th style={{ padding: 8 }}>Name / E-Mail</th>
                  <th style={{ padding: 8 }}>Rolle</th>
                  <th style={{ padding: 8 }}>FO/BO</th>
                  <th style={{ padding: 8 }}>Team(s)</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {pendingUsers.map((u) => (
                  <PendingUserRow
                    key={u.id}
                    user={u}
                    teams={teams}
                    onApproved={loadAll}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section>
        <Text weight="semibold" size={400}>Benutzerübersicht</Text>
        <Text size={200} style={{ display: "block", color: "#666", marginBottom: 8 }}>
          Klick auf einen Benutzer öffnet die Detailansicht.
        </Text>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                <th style={{ padding: 8 }}>Name (Nachname, Vorname)</th>
                <th style={{ padding: 8 }}>Kürzel</th>
                <th style={{ padding: 8 }}>E-Mail</th>
                <th style={{ padding: 8 }}>Rolle</th>
                <th style={{ padding: 8 }}>FO/BO</th>
                <th style={{ padding: 8 }}>Team(s)</th>
                <th style={{ padding: 8 }}></th>
              </tr>
            </thead>
            <tbody>
              {activeUsers.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: 12, color: "#666" }}>Keine Benutzer</td></tr>
              ) : (
                activeUsers.map((u) => {
                  const memberships = teamsByUserId.get(u.id) ?? [];
                  return (
                    <tr key={u.id} style={{ borderBottom: "1px solid #f1f1f1" }}>
                      <td style={{ padding: 8 }}>{userDisplay(u)}</td>
                      <td style={{ padding: 8 }}>{u.username ?? "—"}</td>
                      <td style={{ padding: 8 }}>{u.email ?? "—"}</td>
                      <td style={{ padding: 8 }}>{u.role ?? "—"}</td>
                      <td style={{ padding: 8 }}>{u.department ?? "—"}</td>
                      <td style={{ padding: 8 }}>
                        {memberships.length === 0
                          ? "—"
                          : memberships.map((m) => `${m.team.name} (${m.teamRole})`).join(", ")}
                      </td>
                      <td style={{ padding: 8, textAlign: "right" }}>
                        <Button size="small" onClick={() => setView({ kind: "user", userId: u.id })}>
                          Details
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <Text weight="semibold" size={400}>Teamübersicht</Text>
        <Text size={200} style={{ display: "block", color: "#666", marginBottom: 8 }}>
          Klick auf ein Team öffnet die Detailansicht mit Mitgliedern und Berechtigungen.
        </Text>
        {teams.length === 0 ? (
          <Text size={200}>Noch keine Teams angelegt.</Text>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr style={{ background: "#f5f5f5", textAlign: "left" }}>
                  <th style={{ padding: 8 }}>Team</th>
                  <th style={{ padding: 8 }}>Beschreibung</th>
                  <th style={{ padding: 8 }}>Mitglieder</th>
                  <th style={{ padding: 8 }}>Berechtigungen</th>
                  <th style={{ padding: 8 }}></th>
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.id} style={{ borderBottom: "1px solid #f1f1f1" }}>
                    <td style={{ padding: 8, fontWeight: 600 }}>{t.name}</td>
                    <td style={{ padding: 8, color: "#666" }}>{t.description ?? "—"}</td>
                    <td style={{ padding: 8 }}>
                      {t.members.length === 0
                        ? "—"
                        : t.members
                            .map((m) => {
                              const u = usersById.get(m.userId);
                              return u ? `${userDisplay(u)} (${m.teamRole})` : m.userId;
                            })
                            .join(", ")}
                    </td>
                    <td style={{ padding: 8 }}>{t.permissions.length}</td>
                    <td style={{ padding: 8, textAlign: "right" }}>
                      <Button size="small" onClick={() => setView({ kind: "team", teamId: t.id })}>
                        Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <UserCreateDialog
        open={showCreateUser}
        teams={teams.map((t) => ({ id: t.id, name: t.name }))}
        onClose={() => setShowCreateUser(false)}
        onCreated={loadAll}
      />
      <TeamCreateDialog
        open={showCreateTeam}
        users={users}
        onClose={() => setShowCreateTeam(false)}
        onCreated={loadAll}
      />
    </Card>
  );
};

const Header: React.FC<{ onBack: () => void; title: string; subtitle?: string }> = ({
  onBack,
  title,
  subtitle,
}) => (
  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
    <Button appearance="subtle" icon={<ArrowLeft20Regular />} onClick={onBack}>
      Zurück
    </Button>
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Subtitle1>{title}</Subtitle1>
      {subtitle && <Text size={200} style={{ color: "#666" }}>{subtitle}</Text>}
    </div>
  </div>
);

/**
 * Inline user-detail card. Replaces the previous UserEditDialog popup —
 * fields are editable on the same page; "Speichern" persists via better-auth
 * admin client (role) + the custom PUT /api/admin/users/:id endpoint (name fields).
 *
 * The effective permission grid stays read-only here (per-user perms don't
 * exist in the model — they derive from team membership). To grant or revoke
 * permissions, navigate into a team via "Zum Team".
 */
const UserDetailCard: React.FC<{
  user: RawUser;
  memberships: Array<{ team: AppTeamDto; teamRole: TeamRole }>;
  effectivePermissions: Set<string>;
  onBack: () => void;
  onOpenTeam: (teamId: string) => void;
  onSaved: () => void | Promise<void>;
  onDeleted: () => void | Promise<void>;
}> = ({ user, memberships, effectivePermissions, onBack, onOpenTeam, onSaved, onDeleted }) => {
  const [firstName, setFirstName] = useState(user.firstName ?? "");
  const [lastName, setLastName] = useState(user.lastName ?? "");
  const [username, setUsername] = useState(user.username ?? "");
  const [email, setEmail] = useState(user.email ?? "");
  const [role, setRole] = useState<AppRole>((user.role as AppRole) ?? "mitarbeiter");
  const [department, setDepartment] = useState<"FO" | "BO" | "FO+BO">(
    (user.department as "FO" | "BO" | "FO+BO") ?? "FO",
  );
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Reset local state if a different user is shown.
  useEffect(() => {
    setFirstName(user.firstName ?? "");
    setLastName(user.lastName ?? "");
    setUsername(user.username ?? "");
    setEmail(user.email ?? "");
    setRole((user.role as AppRole) ?? "mitarbeiter");
    setDepartment((user.department as "FO" | "BO") ?? "FO");
    setDirty(false);
  }, [user.id]);

  const markDirty = () => setDirty(true);
  const allChecked = isPrivilegedRole(user.role);

  const handleSave = async () => {
    setSaving(true);
    try {
      const originalRole = (user.role as AppRole) ?? "mitarbeiter";
      if (role !== originalRole) {
        const { error } = await authClient.admin.setRole({
          userId: user.id,
          role: role as any,
        });
        if (error) {
          void hinweisen({ titel: "Rolle nicht geändert", text: error?.message || "Die Rolle konnte nicht geändert werden.", ton: "danger" });
          return;
        }
      }
      const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
      const payload = { firstName, lastName, username, department, email, name: fullName || username || email };

      const adminAny = authClient.admin as any;
      let updated = false;
      if (typeof adminAny.updateUser === "function") {
        try {
          const { error } = await adminAny.updateUser({ userId: user.id, data: payload });
          if (!error) updated = true;
        } catch {
          // fall through
        }
      }
      if (!updated) {
        const res = await fetch(apiUrl(`admin/users/${user.id}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          void hinweisen({ titel: "Speichern fehlgeschlagen", text: err?.message || "Die Benutzerdaten konnten nicht gespeichert werden.", ton: "danger" });
          return;
        }
      }
      setDirty(false);
      await onSaved();
    } finally {
      setSaving(false);
    }
  };

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ").trim() || user.name || user.email || user.id;

  return (
    <Card style={{ padding: 20, display: "flex", flexDirection: "column", gap: 20 }}>
      <Header onBack={onBack} title={`Benutzer: ${fullName}`} subtitle={user.email} />

      <section>
        <Text weight="semibold" size={400}>Stammdaten</Text>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 8 }}>
          <Field label="Vorname">
            <Input value={firstName} onChange={(_, d) => { setFirstName(d.value); markDirty(); }} />
          </Field>
          <Field label="Nachname">
            <Input value={lastName} onChange={(_, d) => { setLastName(d.value); markDirty(); }} />
          </Field>
          <Field label="Kürzel">
            <Input value={username} onChange={(_, d) => { setUsername(d.value); markDirty(); }} />
          </Field>
          <Field label="E-Mail">
            <Input type="email" value={email} onChange={(_, d) => { setEmail(d.value); markDirty(); }} />
          </Field>
          <Field label="Rolle">
            <Select value={role} onChange={(_, d) => { setRole(d.value as AppRole); markDirty(); }}>
              <option value="admin">Admin</option>
              <option value="gfgl">Geschäftsführung/-leitung (gfgl)</option>
              <option value="mitarbeiter">Mitarbeiter</option>
              <option value="service">Service</option>
            </Select>
          </Field>
          <Field label="FO/BO">
            <Select value={department} onChange={(_, d) => { setDepartment(d.value as "FO" | "BO" | "FO+BO"); markDirty(); }}>
              <option value="FO">Front-Office (FO)</option>
              <option value="BO">Back-Office (BO)</option>
              <option value="FO+BO">Front- und Back-Office (FO+BO)</option>
            </Select>
          </Field>
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
          <Button appearance="primary" onClick={handleSave} disabled={saving || !dirty}>
            {saving ? <Spinner size="tiny" /> : "Speichern"}
          </Button>
          <Button appearance="subtle" onClick={onDeleted}>Benutzer löschen</Button>
        </div>
      </section>

      <section>
        <Text weight="semibold" size={400}>Teams</Text>
        {memberships.length === 0 ? (
          <Text size={200} style={{ display: "block", color: "#666", marginTop: 6 }}>
            Keine Team-Mitgliedschaft.
          </Text>
        ) : (
          <ul style={{ marginTop: 6, paddingLeft: 20 }}>
            {memberships.map((m) => (
              <li key={m.team.id} style={{ marginBottom: 4 }}>
                {m.team.name} · <em>{m.teamRole}</em>{" "}
                <Button size="small" appearance="subtle" onClick={() => onOpenTeam(m.team.id)}>
                  Zum Team →
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section>
        <Text weight="semibold" size={400}>Effektive Berechtigungen</Text>
        <Text size={200} style={{ display: "block", color: "#666", marginBottom: 6 }}>
          {allChecked
            ? "Vollzugriff aufgrund der System-Rolle (admin/gfgl)."
            : user.role === "service"
            ? "Service-Rolle: Kontakte anlegen und bearbeiten (nicht löschen), alle Vorgänge sehen, eigene und Team-Vorgänge bearbeiten — laut Rechte-Matrix."
            : "Vereinigung aller Team-Berechtigungen. Zum Bearbeiten zum jeweiligen Team navigieren."}
        </Text>
        <PermissionGrid selected={effectivePermissions} readOnly allChecked={allChecked} />
      </section>
    </Card>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
    <Label>{label}</Label>
    {children}
  </div>
);

const AddMemberInline: React.FC<{
  teamId: string;
  candidates: RawUser[];
  onAdd: (teamId: string, userId: string, teamRole: TeamRole) => Promise<void>;
}> = ({ teamId, candidates, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [userId, setUserId] = useState("");
  const [teamRole, setTeamRole] = useState<TeamRole>("teammitglied");
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <Button
        size="small"
        onClick={() => {
          setUserId(candidates[0]?.id ?? "");
          setTeamRole("teammitglied");
          setOpen(true);
        }}
      >
        Mitglied hinzufügen
      </Button>
    );
  }

  return (
    <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
      <Select value={userId} onChange={(_, d) => setUserId(d.value)}>
        {candidates.length === 0 && <option value="">— alle Benutzer im Team —</option>}
        {candidates.map((u) => (
          <option key={u.id} value={u.id}>
            {[u.firstName, u.lastName].filter(Boolean).join(" ") || u.name || u.email}
          </option>
        ))}
      </Select>
      <Select value={teamRole} onChange={(_, d) => setTeamRole(d.value as TeamRole)}>
        <option value="teammitglied">Teammitglied</option>
        <option value="teamleiter">Teamleiter</option>
      </Select>
      <Button
        size="small"
        appearance="primary"
        disabled={!userId || busy}
        onClick={async () => {
          setBusy(true);
          await onAdd(teamId, userId, teamRole);
          setBusy(false);
          setOpen(false);
        }}
      >
        Hinzufügen
      </Button>
      <Button size="small" appearance="subtle" onClick={() => setOpen(false)}>
        Abbrechen
      </Button>
    </div>
  );
};

/**
 * One row in the "Wartet auf Freigabe" table. Lets an admin set role +
 * department + optional team memberships and then atomically (best-effort,
 * since better-auth and the RBAC API are separate) approve the user via:
 *   1. authClient.admin.setRole — change role on the better-auth user
 *   2. PUT /admin/users/:id     — persist department (additionalField)
 *   3. POST /rbac/teams/:id/members for each selected team
 *   4. authClient.admin.unbanUser — clear banned + banReason
 * No transactional rollback; if step 3 fails for some teams the admin can
 * re-try by approving again (idempotent) or fix membership manually.
 */
const PendingUserRow: React.FC<{
  user: RawUser;
  teams: AppTeamDto[];
  onApproved: () => void | Promise<void>;
}> = ({ user, teams, onApproved }) => {
  const [role, setRole] = useState<AppRole>("mitarbeiter");
  const [department, setDepartment] = useState<"FO" | "BO" | "FO+BO">("FO");
  const [teamIds, setTeamIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const toggleTeam = (id: string, checked: boolean) =>
    setTeamIds((prev) => (checked ? [...prev, id] : prev.filter((t) => t !== id)));

  const approve = async () => {
    setBusy(true);
    try {
      const { error: roleErr } = await authClient.admin.setRole({
        userId: user.id,
        role: role as any,
      });
      if (roleErr) {
        void hinweisen({ titel: "Rolle nicht gesetzt", text: roleErr.message || "Die Rolle konnte nicht gesetzt werden.", ton: "danger" });
        return;
      }

      const deptRes = await fetch(apiUrl(`admin/users/${user.id}`), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ department }),
      });
      if (!deptRes.ok) {
        void hinweisen({ titel: "Abteilung nicht gespeichert", text: "Die Abteilung (FO/BO) konnte nicht gespeichert werden.", ton: "danger" });
        return;
      }

      for (const teamId of teamIds) {
        await fetch(apiUrl(`rbac/teams/${teamId}/members`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId: user.id, teamRole: "teammitglied" }),
        });
      }

      const adminAny = authClient.admin as any;
      if (typeof adminAny.unbanUser !== "function") {
        void hinweisen({ titel: "Freischalten nicht möglich", text: "Diese Version der Anmeldung (Better Auth) kann Benutzer nicht entsperren.", ton: "danger" });
        return;
      }
      const { error: unbanErr } = await adminAny.unbanUser({ userId: user.id });
      if (unbanErr) {
        void hinweisen({ titel: "Freischalten fehlgeschlagen", text: unbanErr.message || "Der Benutzer konnte nicht entsperrt werden.", ton: "danger" });
        return;
      }

      await onApproved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr style={{ borderBottom: "1px solid #f1f1f1" }}>
      <td style={{ padding: 8 }}>
        <div><strong>{user.name || user.email || user.id}</strong></div>
        <div style={{ color: "#666", fontSize: 12 }}>{user.email ?? "—"}</div>
      </td>
      <td style={{ padding: 8 }}>
        <Select value={role} onChange={(_, d) => setRole(d.value as AppRole)}>
          <option value="mitarbeiter">Mitarbeiter</option>
          <option value="gfgl">Geschäftsführung (gfgl)</option>
          <option value="service">Service</option>
          <option value="admin">Admin</option>
        </Select>
      </td>
      <td style={{ padding: 8 }}>
        <Select
          value={department}
          onChange={(_, d) => setDepartment(d.value as "FO" | "BO" | "FO+BO")}
        >
          <option value="FO">FO</option>
          <option value="BO">BO</option>
          <option value="FO+BO">FO+BO</option>
        </Select>
      </td>
      <td style={{ padding: 8 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            maxHeight: 110,
            overflowY: "auto",
          }}
        >
          {teams.length === 0 && <Text size={200}>— noch keine Teams —</Text>}
          {teams.map((t) => (
            <label key={t.id} style={{ fontSize: 13 }}>
              <input
                type="checkbox"
                checked={teamIds.includes(t.id)}
                onChange={(e) => toggleTeam(t.id, e.target.checked)}
              />{" "}
              {t.name}
            </label>
          ))}
        </div>
      </td>
      <td style={{ padding: 8, textAlign: "right" }}>
        <Button appearance="primary" disabled={busy} onClick={approve}>
          {busy ? <Spinner size="tiny" /> : "Freigeben"}
        </Button>
      </td>
    </tr>
  );
};

export default UserManagementSection;
